import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CombinedQuerySuggestion } from '../../../@types/QuerySuggestion';
import QueryFilter from '../../../helpers/query-filter';
import type { PrettyHistory } from '../../structures/MainSearch/usePrettyHistory';

import { resolveArticlesRoute } from '../../../routes/utils';
import { Button } from '../Button';
import { IconClose } from '../Icon';
import { SrOnly } from '../SrOnly';
import SuggestionsList from '../SuggestionList';
import { ClearButton, Inner, Input, InputWrapper, Meta, Wrapper } from './wrapper';

interface RichSearchProps {
  placeholder?: string;
  value: string;
  meta?: React.ReactNode | string;
  suggestions?: CombinedQuerySuggestion;
  history?: PrettyHistory[];
  queryFilter?: QueryFilter;
  focusedIndex: number | null;
  inputTestId?: string;
  buttonTestId?: string;
  metaTestId?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSuggestionClick?: () => void;
  onSetFocusedIndex: (index: number | null) => void;
}

const RichSearch = ({
  placeholder,
  value,
  meta,
  suggestions,
  history,
  queryFilter,
  focusedIndex,
  inputTestId,
  buttonTestId,
  metaTestId,
  onChange: _onChange,
  onKeyDown: _onKeyDown,
  onClick: _onClick,
  onClear: _onClear,
  onSuggestionClick: _onSuggestionClick,
  onSetFocusedIndex,
}: RichSearchProps) => {
  const { t } = useTranslation(['common']);
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focus, setFocus] = useState(false);

  const onFocus = () => setFocus(true);

  useEffect(() => {
    if (focusedIndex) {
      onSetFocusedIndex(focusedIndex);
    }
  }, [focusedIndex]);

  useEffect(() => {
    const onDocumentClick = (e: Event) => {
      if (!ref.current?.contains(e.target as unknown as HTMLElement)) {
        setFocus(false);
      }
    };

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFocus(true);
      _onChange?.(e);
    },
    [setFocus, _onChange],
  );

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setFocus(false);
      _onClick(e);
    },
    [setFocus, _onClick],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && focusedIndex === null) {
        setFocus(false);
      }
      _onKeyDown(e);
    },
    [focusedIndex, setFocus, _onKeyDown],
  );

  const onClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      inputRef.current?.focus();
      _onClear(e);
    },
    [setFocus, _onClear],
  );

  const onSuggestionClick = useCallback(() => {
    setFocus(false);
    _onSuggestionClick?.();
  }, [setFocus, _onSuggestionClick]);

  const slicedHistory = useMemo(() => history?.slice(0, 3), [history]);
  const groups = useMemo(() => {
    const _groups = [];

    if (suggestions?.vehicleSuggestions?.length) {
      _groups.push({
        label: t('Search hits', { ns: 'common' }),
        suggestions: suggestions?.vehicleSuggestions.map((item) => ({
          label: item.label,
          hitCount: item.hitCount,
          path: `/sok${(
            queryFilter
              ? QueryFilter.merge(new QueryFilter(item.queryFilter), queryFilter)
              : new QueryFilter(item.queryFilter)
          ).toQueryURL()}`,
        })),
      });
    }

    if (suggestions?.contentSuggestions?.length) {
      _groups.push({
        label: t('articles', { ns: 'article' }),
        suggestions: [
          ...suggestions.contentSuggestions.map((s) => ({
            label: s.label,
            path: s.path,
          })),
          {
            label: t('showAllArticlesFor', { ns: 'article', phrase: value }),
            path: `/${resolveArticlesRoute()}/sok?q=${value}`,
          },
        ],
      });
    }

    if (slicedHistory?.length) {
      _groups.push({
        label: t('Your recent searches', { ns: 'common' }),
        linkPath: '/sok/historik',
        linkLabel: t('Show all', { ns: 'common' }),
        suggestions: slicedHistory.map((item) => ({
          label: item.displayName.join(', '),
          path: `/sok${item.queryFilter.toQueryURL()}`,
        })),
      });
    }
    return _groups;
  }, [suggestions, slicedHistory, value, t]);

  return (
    <Wrapper ref={ref}>
      <Inner>
        <InputWrapper>
          <Input
            type="search"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            ref={inputRef}
            data-testid={inputTestId}
          />
          {value && (
            <ClearButton onClick={onClear} title={t('clearSearchField', { ns: 'common' })}>
              <IconClose />
              <SrOnly>{t('clearSearchField', { ns: 'common' })}</SrOnly>
            </ClearButton>
          )}
          {meta && <Meta data-testid={metaTestId}>{meta}</Meta>}
          <Button
            $tone="primary"
            $size="small"
            onClick={onClick}
            title={t('Search', { ns: 'common' }) || ''}
            data-testid={buttonTestId}
          >
            {t('search', { ns: 'common' })}
          </Button>
        </InputWrapper>
        {focus && groups.length > 0 && (
          <SuggestionsList
            groups={groups}
            focusedIndex={focusedIndex}
            onSetFocusedIndex={onSetFocusedIndex}
            onSuggestionClick={onSuggestionClick}
          />
        )}
      </Inner>
    </Wrapper>
  );
};

export default RichSearch;
